/** @format */

import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import PlatformResellerModify, { PlatformResellerSendType } from './PlatformResellerModify';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { platformUserConstants } from '../../modules/platformuser/actions';
import PlatformResellerInterface from '../../interfaces/platform/PlatformResellerInterface';
import { platformResellerConstants } from '../../modules/platformreseller/actions';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import PlatformResellerDiscount from '../Discount/PlatformResellerDiscount/PlatformResellerDiscount';
import PlatformResellerRateDiscount from '../Discount/PlatformResellerDiscount/PlatformResellerRateDiscount';
import { Auth } from '../../modules/auth/Auth';
import WelcomeSmsPriceList from '../NotificationConfiguration/WelcomeSmsConfiguration/WelcomeSmsPriceList';
import { PlatformResellerMvnoOperatorSetup } from './Components/PlatformResellerMvnoOperatorSetup';
import { PlatformResellerApnPublicList } from './Components/PlatformResellerApnPublicList';
import { PlatformResellerApnPrivateList } from './Components/PlatformResellerApnPrivateList';
import PlatformResellerConnectionLimit from './Components/PlatformResellerConnectionLimit';

// const _ = require('underscore')

export type PlatformResellerEditType = {
    match: any;
};

export const PlatformResellerEdit: FunctionComponent<PlatformResellerEditType> = ({ match }): React.ReactElement => {
    const dispatch = useDispatch();
    const [reloadFlag, setReloadFlag] = useState(true);
    const id = useMemo((): string | null => {
        return match?.params?.id;
    }, [match]);
    const [outputObject, setOutputObject] = useState<PlatformResellerSendType | null>(null);

    const savePlatformReseller = (outputObject: PlatformResellerSendType): void => {
        if (!id) {
            return;
        }
        const itemLoaders = new ItemLoaders(dispatch);

        if (outputObject.rpt?.trim().length !== 5) {
            NotificationHelper.notifyError('platform_reseller:notify.rpt_length_error', dispatch);
            return;
        }

        itemLoaders.putWithCallback(
            FormGeneralHelper.getByUri(id),
            platformResellerConstants.PLATFORM_RESELLER_EDIT,
            outputObject,
            (): void => {
                setOutputObject(null);
                reload();
            },
            {
                dispatchAfterCallback: true,
                dispatchErrorAfterCallback: true,
            }
        );
    };

    const reload = (): void => {
        setReloadFlag(true);
    };

    useEffect(() => {
        if (!id || !reloadFlag) {
            return;
        }

        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getWithCallback(
            FormGeneralHelper.getByUri(id),
            platformUserConstants.ITEM,
            (data: PlatformResellerInterface): void => {
                const tmpOutputObject = {
                    agreementNumber: data.agreementNumber ?? '',
                    mainname: data.mainname ?? '',
                    firstname: data.firstname ?? undefined,
                    mail: data.mail ?? undefined,
                    nip: data.nip ?? '',
                    street: data.street ?? '',
                    apartment: data.apartment ?? undefined,
                    city: data.city ?? '',
                    state: data.state ?? '',
                    country: data.country ?? '',
                    house: data.house ?? '',
                    zip: data.zip ?? '',
                    syncDefaultPortinMsisdnIndicatedRangeId: data.syncDefaultPortinMsisdnIndicatedRangeId ?? undefined,
                    paymentDeadlineCodeId: data.paymentDeadlineCodeId ?? undefined,
                    tariffId: data.tariffId ?? undefined,
                    hostTariffId: data.hostTariffId ?? undefined,
                    ecorpNumber: data.ecorpNumber,
                    ecorpCostNumber: data.ecorpCostNumber,
                    // vat: data.vat,
                    rpt: data.rpt,
                    paymentDisposition: data.paymentDisposition,
                    bankaccount: data.bankaccount ?? '',
                    organizationUnit: data.organizationUnit ?? '',
                    accountingNumber: data.accountingNumber ?? '',
                    parentResellerId: data.parentResellerId,
                    requireVcOnProductActivation: data.requireVcOnProductActivation,
                    platformBillingCustomer: data.platformBillingCustomer,
                    ftpUser: data.ftpUser ?? null,
                    fupBasePeriod: data.fupBasePeriod,
                    fupMonitorDays: data.fupMonitorDays,
                    fupMonitorDaysHitSendSms: data.fupMonitorDaysHitSendSms,
                    fupMonitorDaysHitSmsMsg: data.fupMonitorDaysHitSmsMsg,
                    fupMonitorCheckDays: data.fupMonitorCheckDays,
                    fupMonitorCheckDaysSendSms: data.fupMonitorCheckDaysSendSms,
                    fupMonitorCheckDaysFailsSmsMsg: data.fupMonitorCheckDaysFailsSmsMsg,
                    fupMonitorCheckDaysFailsViolation: data.fupMonitorCheckDaysFailsViolation,
                    fupMonitorCheckDaysFailsViolationSendSms: data.fupMonitorCheckDaysFailsViolationSendSms,
                    fupMonitorCheckDaysFailsViolationSmsMsg: data.fupMonitorCheckDaysFailsViolationSmsMsg,
                    useType: data.useType,
                    allowConfirmationCode: data.allowConfirmationCode,
                    defaultSystemLanguageId: data.defaultSystemLanguageId,
                    invoiceLanguageId: data.invoiceLanguageId,
                    currencyId: data.currencyId,
                    eSimPreActivateAllowed: data.eSimPreActivateAllowed,
                    payerMainname: data.payerMainname ?? '',
                    payerFirstname: data.payerFirstname ?? undefined,
                    payerMail: data.payerMail ?? undefined,
                    invoicingEmail: data.invoicingEmail ?? '',
                    payerNip: data.payerNip ?? '',
                    payerStreet: data.payerStreet ?? '',
                    payerApartment: data.payerApartment ?? undefined,
                    payerCity: data.payerCity ?? '',
                    payerState: data.payerState ?? '',
                    payerCountry: data.payerCountry ?? '',
                    payerHouse: data.payerHouse ?? '',
                    payerZip: data.payerZip ?? '',
                    vat: data.vat ?? '',
                    countryCode: data.countryCode ?? '',
                    payerCountryCode: data.payerCountryCode ?? ''
                } as PlatformResellerSendType;
                setOutputObject(tmpOutputObject);
                setReloadFlag(false);
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true, dispatchErrorAfterCallback: true }
        );
    }, [dispatch, id, reloadFlag]);

    return (
        <div>
            {outputObject && (
                <>
                    <GridContainer spacing={2}>
                        <GridItem xs={12}>
                            <PlatformResellerModify editId={id} outputObject={outputObject} savePlatformReseller={savePlatformReseller} />
                        </GridItem>


                        <GridItem xs={6}>
                            <GridContainer>
                                <GridItem xs={12}>
                                    {<PlatformResellerConnectionLimit resellerId={id}  isRootReseller={false} />}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem xs={6}>
                            <GridContainer>
                                <GridItem xs={12}>
                                    {outputObject && outputObject.parentResellerId === null && <PlatformResellerConnectionLimit resellerId={id}  isRootReseller={true} />}
                                </GridItem>
                            </GridContainer>
                        </GridItem>



                        <GridItem xs={6}>
                            <GridContainer>
                                <GridItem xs={12}>
                                    {Auth.isRootReseller() && <PlatformResellerMvnoOperatorSetup platformResellerId={id} reload={reload} />}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {id && (
                            <GridItem xs={6}>
                                <GridContainer>
                                    <GridItem xs={12}>
                                        <PlatformResellerApnPublicList platformResellerIri={id} />
                                    </GridItem>
                                    <GridItem xs={12}>
                                        <PlatformResellerApnPrivateList platformResellerIri={id} />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        )}
                        {Auth.isRootReseller() && UserRoleHelper.checkRole(roles.ROLE_PLATFORM_DISCOUNT_GET) && (
                            <>
                                <GridItem xs={12}>
                                    <PlatformResellerDiscount platformResellerId={id} />
                                </GridItem>
                                <GridItem xs={12}>
                                    <PlatformResellerRateDiscount platformResellerId={id} />
                                </GridItem>
                                <GridItem xs={12}>
                                    <WelcomeSmsPriceList platformResellerId={id} />
                                </GridItem>
                            </>
                        )}
                    </GridContainer>
                </>
            )}
        </div>
    );
};

export default PlatformResellerEdit;
